import { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RefreshCcw, Search, Shield, FileText, ShoppingBag, CreditCard, Settings, Database, Zap } from "lucide-react";

const ENTITY_ICONS = { order:ShoppingBag, product:FileText, credit:CreditCard, settings:Settings, system:Database };

const ACTION_STYLE = {
  status_change:   { bg:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd"  },
  credit_issued:   { bg:"#ede9fe", color:"#5b21b6", border:"1px solid #c4b5fd"  },
  force_sync:      { bg:"#fef3c7", color:"#92400e", border:"1px solid #fde68a"  },
  settings_update: { bg:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0"  },
  backup_restore:  { bg:"#ffedd5", color:"#9a3412", border:"1px solid #fdba74"  },
  manual_override: { bg:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5"  },
};

const ENTITY_FILTER = [
  { value:"all",     label:"All types"  },
  { value:"order",   label:"Orders"     },
  { value:"product", label:"Products"   },
  { value:"credit",  label:"Credits"    },
  { value:"settings",label:"Settings"   },
  { value:"system",  label:"System"     },
];
const ACTION_FILTER = [
  { value:"all",            label:"All actions"     },
  { value:"status_change",  label:"Status change"   },
  { value:"credit_issued",  label:"Credit issued"   },
  { value:"force_sync",     label:"Force sync"      },
  { value:"settings_update",label:"Settings update" },
  { value:"backup_restore", label:"Backup restore"  },
];

export default function AdminAuditLog() {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [entityType, setEntityType] = useState("all");
  const [action,     setAction]     = useState("all");
  const [search,     setSearch]     = useState("");
  const [limit,      setLimit]      = useState(100);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (entityType !== "all") params.set("entity_type", entityType);
      if (action     !== "all") params.set("action", action);
      const { data } = await adminApi.get(`/admin/audit-log?${params}`);
      setItems(data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [entityType, action, limit]);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? items.filter(i =>
        i.action?.includes(search.toLowerCase()) ||
        i.entity_id?.includes(search) ||
        i.performed_by?.toLowerCase().includes(search.toLowerCase()) ||
        JSON.stringify(i.details||{}).toLowerCase().includes(search.toLowerCase())
      )
    : items;

  const inp = { background:"#ffffff", borderColor:"#dde3ef", color:"#0f172a" };

  return (
    <div className="space-y-5" data-testid="admin-audit-log-page">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Compliance</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Audit Log</h1>
          <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
            All admin actions — status changes, credits, force syncs, settings
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-4 h-9 rounded-xl text-[12px] font-semibold"
          style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
          <RefreshCcw className={`h-3.5 w-3.5 ${loading?"animate-spin":""}`}/> Refresh
        </button>
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color:"#94a3b8" }}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search action, entity ID, user…"
            className="w-full pl-9 h-10 rounded-xl text-[13px] outline-none"
            style={{ background:"#ffffff", border:"1px solid #dde3ef", color:"#0f172a" }}/>
        </div>
        {/* Entity type */}
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="w-44 rounded-xl" style={inp}><SelectValue/></SelectTrigger>
          <SelectContent>
            {ENTITY_FILTER.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Action */}
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-44 rounded-xl" style={inp}><SelectValue/></SelectTrigger>
          <SelectContent>
            {ACTION_FILTER.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Limit */}
        <Select value={String(limit)} onValueChange={v=>setLimit(Number(v))}>
          <SelectTrigger className="w-32 rounded-xl" style={inp}><SelectValue/></SelectTrigger>
          <SelectContent>
            {[50,100,200,500].map(n=><SelectItem key={n} value={String(n)}>Last {n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Timeline table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto" style={{ border:"1px solid #dde3ef" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {["Time","Action","Entity","Performed by","Details"].map(h=>(
                <th key={h} className="py-3 px-4 font-mono font-semibold uppercase text-left"
                  style={{ color:"#475569", fontSize:10, letterSpacing:"0.15em" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((it, i) => {
              const Icon    = ENTITY_ICONS[it.entity_type] || Shield;
              const actStyle= ACTION_STYLE[it.action] || { bg:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0" };
              return (
                <tr key={it.id||i} className="hover:bg-[#f5f7fb] transition-colors"
                  style={{ borderBottom: i < filtered.length-1 ? "1px solid #f0f4f8" : "none" }}>

                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-[11px] font-mono" style={{ color:"#0f172a" }}>
                      {it.created_at ? new Date(it.created_at).toLocaleString("en-AU") : "—"}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                      style={{ background:actStyle.bg, color:actStyle.color, border:actStyle.border }}>
                      {(it.action||"—").replace(/_/g," ")}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background:"#f5f7fb", border:"1px solid #dde3ef" }}>
                        <Icon className="h-3 w-3" style={{ color:"#64748b" }}/>
                      </div>
                      <div>
                        <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                          {it.entity_type}
                        </div>
                        {it.entity_id && (
                          <div className="text-[11px] font-mono" style={{ color:"#475569" }}>
                            {it.entity_id.slice(0,14)}…
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ background:"#1e5bb8" }}>
                        {(it.performed_by||"?")[0].toUpperCase()}
                      </div>
                      <span className="text-[12px]" style={{ color:"#0f172a" }}>{it.performed_by||"system"}</span>
                    </div>
                  </td>

                  <td className="px-4 py-3 max-w-[220px]">
                    {it.details && Object.keys(it.details).length > 0 ? (
                      <details className="cursor-pointer">
                        <summary className="text-[11px] font-mono" style={{ color:"#1e5bb8" }}>
                          View details
                        </summary>
                        <pre className="mt-1 text-[10px] font-mono p-2 rounded-lg overflow-auto max-h-32"
                          style={{ background:"#f5f7fb", color:"#475569", border:"1px solid #dde3ef" }}>
                          {JSON.stringify(it.details, null, 2)}
                        </pre>
                      </details>
                    ) : <span style={{ color:"#94a3b8" }}>—</span>}
                  </td>
                </tr>
              );
            })}
            {filtered.length===0 && !loading && (
              <tr><td colSpan={5} className="py-14 text-center">
                <Shield className="h-10 w-10 mx-auto mb-3" style={{ color:"#c5cfe3" }}/>
                <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                  {search?"No matching entries":"No audit entries yet"}
                </div>
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="py-8 text-center text-[12px] font-mono uppercase tracking-widest animate-pulse" style={{ color:"#94a3b8" }}>
                Loading…
              </td></tr>
            )}
          </tbody>
        </table>
        <div className="px-4 py-2.5 text-right text-[11px] font-mono" style={{ borderTop:"1px solid #f1f5f9", color:"#94a3b8" }}>
          {filtered.length} entries shown
        </div>
      </div>
    </div>
  );
}